import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import BreakingNews from "../../components/BreakingNews";
import RightSidebar from "../../components/RightSidebar";
import NewsCard from "../../components/NewsCard";
import Profilecard from "../../components/Profilecard";

const NewsDetail = () => {
  const { id } = useParams();

  const [news, setNews] = useState(null);
  const [moreNews, setMoreNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    // 🔥 Scroll to top
    window.scrollTo({ top: 0, behavior: "auto" });

    // 🔥 Stop any running speech safely before loading new page
    try {
      if (
        typeof window !== "undefined" &&
        "speechSynthesis" in window &&
        window.speechSynthesis
      ) {
        window.speechSynthesis.cancel();
      }
    } catch (_) {}

    setLoading(true);
    setFetchError(false);

    // ================= FETCH MAIN NEWS =================
    const mainPromise = axios
      .get(`${process.env.REACT_APP_BACKEND_URL}product/one/${id}`)
      .then((res) => {
        setNews(res.data);
      })
      .catch((err) => {
        console.error("Error fetching news:", err);
        setFetchError(true);
      });

    // ================= FETCH MORE NEWS =================
    const morePromise = axios
      .get(`${process.env.REACT_APP_BACKEND_URL}product`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setMoreNews(res.data.slice(0, 6));
        }
      })
      .catch((err) => {
        console.error("Error fetching more news:", err);
      });

    Promise.allSettled([mainPromise, morePromise]).finally(() => {
      setLoading(false);
    });

    // 🔥 Cleanup on unmount
    return () => {
      try {
        if (
          typeof window !== "undefined" &&
          "speechSynthesis" in window &&
          window.speechSynthesis
        ) {
          window.speechSynthesis.cancel();
        }
      } catch (_) {}
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center p-8">
        <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-gray-600 text-sm font-medium">खबर लोड हो रही है...</p>
      </div>
    );
  }

  if (fetchError || !news) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-white border border-red-200 rounded-xl p-6 max-w-md w-full shadow-sm">
          <i className="fa-solid fa-triangle-exclamation text-3xl text-amber-500 mb-3"></i>
          <h2 className="text-lg font-bold text-gray-800 mb-2">खबर लोड नहीं हो सकी</h2>
          <p className="text-xs text-gray-500 mb-4">कृपया पेज रिफ्रेश करें या होमपेज पर जाएँ।</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-700 transition"
            >
              दोबारा प्रयास करें
            </button>
            <a
              href="/"
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition"
            >
              होमपेज पर जाएँ
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Helpers ──────────────────────────────────────────────────────────
  const backendBase = process.env.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL.replace('/api/v1/', '').replace('/api/v1', '')
    : '';

  // Build image URL safely: each path segment encoded individually
  // so folder separators (/) are NOT percent-encoded.
  const thumbnailUrl = news.thumbnail
    ? `${backendBase}/uploads/news/${news.thumbnail
        .replace(/\\/g, '/')
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`
    : `${window.location.origin}/favicon.png`;

  // Strip HTML tags from description for OG / Twitter meta
  const plainDescription = news.description
    ? news.description.replace(/<[^>]*>/g, '').trim().slice(0, 160)
    : 'मध्य प्रदेश और छत्तीसगढ़ की ताज़ा खबरें हिंदी और बुंदेली में।';

  const pageUrl = `${window.location.origin}/news/${news.id}`;

  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>{news.news_title} | Bundeli Chugli</title>

        <meta name="description" content={plainDescription} />

        {/* Open Graph — NOTE: These only work for real browsers.
            Social bots (Facebook/WhatsApp/Instagram) are intercepted
            at the server level (.htaccess → backend ogMiddleware)
            before React even loads. */}
        <meta property="og:type"              content="article" />
        <meta property="og:site_name"         content="Bundeli Chugli" />
        <meta property="og:title"             content={news.news_title} />
        <meta property="og:description"       content={plainDescription} />
        <meta property="og:url"               content={pageUrl} />
        <meta property="og:image"             content={thumbnailUrl} />
        <meta property="og:image:secure_url"  content={thumbnailUrl} />
        <meta property="og:image:width"       content="1200" />
        <meta property="og:image:height"      content="630" />
        <meta property="og:image:alt"         content={news.news_title} />

        {/* Twitter / X Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={news.news_title} />
        <meta name="twitter:description" content={plainDescription} />
        <meta name="twitter:image"       content={thumbnailUrl} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 pt-2 pb-6">
        <BreakingNews />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
          {/* ================= MAIN ARTICLE ================= */}
          <div className="lg:col-span-9">
            <Profilecard data={news} />
          </div>

          {/* ================= SIDEBAR ================= */}
          <RightSidebar />
        </div>

        {/* ================= MORE NEWS ================= */}
        <section className="mt-12">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 text-red-600">
            और खबरें
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {moreNews.map((item) => (
              <NewsCard
                key={item.id}
                title={item.news_title}
                image={`${backendBase}/uploads/news/${encodeURIComponent(item.thumbnail)}`}
                category={item.category}
                date={item.createdAt}
                fullNews={item.description}
                author="Bundeli Chugli"
                readTime="2 Min Read"
                link={`/news/${item.id}`}
                videoUrl={item.youtube_url}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default NewsDetail;
